import type {
  OutputDescription,
  ValueConfig,
  ValueConfig_V,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import { useRootStore } from "../../../../../../../../../stores/hooks";

interface ValueTypeFunctions {
  getInputValueTypeByValue: (config: ValueConfig, index: number) => string;
}

export const useValueType = (): ValueTypeFunctions => {
  const { automations } = useRootStore();
  const { candidate } = automations;
  const { trigger } = candidate.self;
  const actions = candidate.allActions;

  const getInputValueTypeByValue = (
    config: ValueConfig,
    index: number,
  ): string => {
    const item = config.values[index];
    let inputs: ValueConfig_V[] = [];
    if (item.value.oneofKind === "keyValue") {
      if (item.value.keyValue.value?.values === undefined) {
        return "";
      }
      inputs = item.value.keyValue.value?.values;
    } else {
      inputs = config.values;
    }

    if (inputs.some((value) => value.value.oneofKind === "string")) {
      return "text";
    }

    if (inputs.some((value) => value.value.oneofKind === "number")) {
      return "number";
    }

    if (inputs.some((value) => value.value.oneofKind === "bool")) {
      return "boolean";
    }

    if (inputs.some((value) => value.value.oneofKind === "list")) {
      return "json_array";
    }

    if (inputs.some((v) => v.value.oneofKind === "reference")) {
      const reference = inputs.find((v) => v.value.oneofKind === "reference");
      if (reference?.value.oneofKind === "reference") {
        const outputId = reference.value.reference.outputId;
        const source = reference.value.reference.source;

        let output: OutputDescription | undefined;
        if (source.oneofKind === "triggerReference") {
          const triggerOutputs =
            trigger.description?.recordListOutput !== undefined
              ? [
                  ...trigger.description.recordListOutput.fields,
                  ...(trigger.description?.outputs ?? []),
                ]
              : (trigger.description?.outputs ?? []);
          output = triggerOutputs.find((o) => o.outputId === outputId);
        } else if (source.oneofKind === "actionReference") {
          const action = actions.find(
            (a) =>
              a.actionReference?.actionId ===
                source.actionReference?.actionId &&
              a.actionReference?.instanceId ===
                source.actionReference?.instanceId,
          );
          const actionOutputs =
            action?.description?.recordListOutput !== undefined
              ? [
                  ...action.description.recordListOutput.fields,
                  ...(action.description?.outputs ?? []),
                ]
              : (action?.description?.outputs ?? []);
          output = actionOutputs.find((o) => o.outputId === outputId);
        }

        if (output?.value?.type !== undefined) {
          switch (output.value.type) {
            case ValueDescription_T.KEY_VALUE:
              return "json_object";
            case ValueDescription_T.LIST:
              return "list";
            case ValueDescription_T.STRING:
              return "text";
            case ValueDescription_T.BOOL:
              return "boolean";
            case ValueDescription_T.NUMBER:
              return "number";
            default:
              return "text";
          }
        }
      }
      // this happens when the reference is not found
      return "text";
    }

    if (inputs.some((v) => v.value.oneofKind === "keyValue")) {
      return "json_object";
    }

    if (inputs.length === 0) {
      return "json_object";
    }

    return "";
  };

  return {
    getInputValueTypeByValue,
  };
};
